/*
 * PLP — reponer el estado al volver con Atras (o al recargar)
 *
 * "CARGAR MAS" agrega productos al DOM pero no toca la URL ni el historial, asi
 * que lo unico que preservaba ese estado al volver de una ficha era el **bfcache**
 * del navegador. El bfcache es una optimizacion sin garantias: se desaloja por
 * presion de memoria, lo apaga de plano cualquier respuesta con `no-store`, y
 * cada navegador tiene sus propias reglas. Cuando falla, el documento se vuelve
 * a pedir al servidor y trae SOLO la pagina 1, mientras el navegador intenta
 * restaurar un scroll que habia medido sobre un documento varias veces mas alto:
 * la pagina queda clavada al fondo (el footer) o arriba de todo.
 *
 * Este archivo guarda cuantas paginas habia cargadas y en que parte de la grilla
 * estaba el visitante, y lo repone cuando la navegacion es `back_forward` o
 * `reload`. Si el bfcache SI funciona, este script **no se vuelve a ejecutar**
 * —el documento se restaura entero, con su JS vivo—, asi que no hay trabajo
 * duplicado ni fetches de mas.
 *
 * Va sin `defer` a proposito, y eso es la mitad del diseño: tiene que poder poner
 * `history.scrollRestoration` en `manual` y **disparar los fetch** antes de que la
 * pagina termine de cargar.
 *
 * ## Por que corre tan temprano: los 3 segundos que habia que sacarle
 *
 * La primera version esperaba a `DOMContentLoaded` para todo y hacia
 * `reinit(); scrollBack()`. Medido sobre un reload de `/collections/mujer` con 3
 * paginas cargadas (muestreo por frame, 349 frames):
 *
 *   t=    0  scrollY 0
 *   t=  638  readyState interactive
 *   t= 1080  DOMContentLoaded        <- recien aca arrancaban los fetch
 *   t= 1735  terminan los fetch
 *   t= 3011  se aplica el scroll     <- 1276ms de insertar + inicializar 48 Swipers
 *
 * O sea que el visitante veia la coleccion **desde arriba, con 24 productos,
 * durante 3 segundos** y recien ahi saltaba. Eso es lo que el cliente reporto
 * como "me lleva arriba de todo": no es que no restaure, es que tarda tanto que
 * nadie espera. Los tres tramos se atacan asi:
 *
 *  1. Los fetch arrancan **durante el parseo**, no en `DOMContentLoaded`. Las URLs
 *     se arman desde `location`, porque los inputs del pager estan despues de la
 *     grilla en el DOM y todavia no existen.
 *  2. Se pide **solo la seccion** (`?section_id=…`), no el documento entero:
 *     484 KB contra 781 KB por pagina, y muchisimo menos para el `DOMParser`.
 *  3. El scroll va **antes** del `reinit()`, que es sincrono y bloquea ~1.2s. El
 *     ancla solo necesita que las cards esten en el DOM.
 *
 * Y para que el visitante no vea el salto, apenas aparece la grilla se le reserva
 * la altura que tenia (`gridHeight`) y se scrollea de una: aterriza en la fila que
 * venia mirando y el hueco de arriba se completa solo.
 */
(() => {
  const GRID = '.load-more-grid';
  const CARD = '.collection-product-card';
  // El handle no vive en el `<li>` de la grilla sino en el `.card-wrapper` de
  // adentro (`snippets/product-card.liquid`), asi que el ancla se busca por el
  // atributo y se sube al `<li>`, que es el que tiene la caja de la celda.
  const HANDLE = '[data-product]';
  const PREFIX = 'ig:plp:';
  const TTL = 30 * 60 * 1000;
  const MAX_PAGES = 20;

  // El id de seccion llega como data-attribute del propio `<script>`: al momento
  // de parsear, `#product-grid[data-id]` —de donde lo saca `facets.js`— todavia
  // no existe, porque esta mas abajo en el documento.
  const SECTION_ID = (document.currentScript && document.currentScript.dataset.sectionId) || '';

  // La clave se recalcula en cada uso y NO se captura una vez: `facets.js` hace
  // `replaceState` al cargar para normalizar la URL (le agrega la clausula de
  // disponibilidad) y `pushState` cada vez que se toca un filtro, asi que la
  // `location.search` del final del documento no es la del principio. Guardando
  // con la del arranque, una coleccion filtrada escribia el estado bajo una clave
  // que al volver ya no existia — medido: tras Atras volvia a 24 cards.
  const keyFor = () => PREFIX + window.location.pathname + window.location.search;

  const store = {
    get(k) {
      try {
        const raw = window.sessionStorage.getItem(k);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    },
    set(k, value) {
      try {
        window.sessionStorage.setItem(k, JSON.stringify(value));
      } catch (e) {
        /* modo privado o cuota: el tema sigue andando sin la restauracion */
      }
    },
    remove(k) {
      try {
        window.sessionStorage.removeItem(k);
      } catch (e) {}
    },
    prune() {
      try {
        const now = Date.now();
        for (let i = window.sessionStorage.length - 1; i >= 0; i--) {
          const k = window.sessionStorage.key(i);
          if (!k || k.indexOf(PREFIX) !== 0) continue;
          const v = store.get(k);
          if (!v || !v.t || now - v.t > TTL) window.sessionStorage.removeItem(k);
        }
      } catch (e) {}
    },
  };

  // `performance.navigation` esta deprecado pero es el unico camino en navegadores
  // viejos; el moderno es la entrada de tipo `navigation` del Performance Timeline.
  const navType = () => {
    const entry =
      typeof performance.getEntriesByType === 'function'
        ? performance.getEntriesByType('navigation')[0]
        : null;
    if (entry && entry.type) return entry.type;
    const legacy = performance.navigation && performance.navigation.type;
    if (legacy === 2) return 'back_forward';
    if (legacy === 1) return 'reload';
    return 'navigate';
  };

  const restorable = (state) =>
    !!state &&
    state.pages > 1 &&
    Date.now() - (state.t || 0) < TTL &&
    (navType() === 'back_forward' || navType() === 'reload');

  const num = (node, fallback) => {
    const parsed = parseInt(node && node.value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  // ---------------------------------------------------------------- fetch
  // Las URLs se arman desde `location` y no desde `[data-next-url]`, que todavia
  // no existe cuando esto corre. Es la misma forma que produce `paginate.next.url`.
  const pageUrl = (page) => {
    const params = new URLSearchParams(window.location.search);
    params.set('page', String(page));
    if (SECTION_ID) params.set('section_id', SECTION_ID);
    return window.location.pathname + '?' + params.toString();
  };

  const fetchPages = (from, to) => {
    const pages = [];
    for (let p = from; p <= to; p++) pages.push(p);
    return {
      pages,
      promise: Promise.all(
        pages.map((p) =>
          fetch(pageUrl(p), { credentials: 'same-origin' })
            .then((res) => (res.ok ? res.text() : ''))
            .catch(() => '')
        )
      ),
    };
  };

  // ------------------------------------------------------------- scroll
  let userMoved = false;
  const markUserMoved = () => {
    userMoved = true;
  };
  ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((evt) =>
    window.addEventListener(evt, markUserMoved, { passive: true, once: true })
  );

  // `behavior: 'instant'` porque `base.css` declara `body { scroll-behavior: smooth }`.
  // Medido que ese valor no se propaga al viewport en Chrome, asi que hoy el salto
  // es instantaneo igual — pero es la misma defensa que ya usa `home-scroll-snap.js:250`
  // y cuesta nada.
  const jumpTo = (y) => window.scrollTo({ top: y, left: 0, behavior: 'instant' });

  const targetY = (state) => {
    let y = Math.max(0, Math.round(state.scrollY || 0));
    // El ancla es mejor que el offset: la grilla puede cambiar de alto entre una
    // visita y la siguiente (un producto que se agoto pierde su linea de cuotas).
    // Se repone la tarjeta que estaba arriba de todo, en el mismo lugar del
    // viewport en el que estaba.
    if (state.anchor) {
      let card = null;
      try {
        const node = document.querySelector(`${GRID} [data-product="${CSS.escape(state.anchor)}"]`);
        card = node ? node.closest(CARD) || node : null;
      } catch (e) {
        card = null;
      }
      if (card) {
        const top = card.getBoundingClientRect().top + window.scrollY;
        y = Math.max(0, Math.round(top - (state.anchorTop || 0)));
      }
    }
    return y;
  };

  const scrollBack = (state) => {
    const y = targetY(state);
    jumpTo(y);
    // Las fotos lazy y el `reinit()` mueven el alto del documento despues del
    // primer layout, asi que el valor se reafirma — pero nunca si el visitante ya
    // se movio por su cuenta. `reinit()` es sincrono, asi que estas dos callbacks
    // corren despues de el y corrigen la posicion final.
    requestAnimationFrame(() => {
      if (!userMoved) jumpTo(targetY(state));
    });
    window.setTimeout(() => {
      if (!userMoved) jumpTo(targetY(state));
    }, 400);
  };

  // --------------------------------------------------------------- pager
  // El pager vive en inputs ocultos (`snippets/load-more.liquid`) y `load-more.js`
  // los lee con jQuery; hay que escribir el `value` Y el `dataset`, en todos los
  // nodos, porque la pagina puede imprimir mas de un juego.
  const writePager = (page, totalPages) => {
    const nextInput = document.querySelector('[data-next-url]');
    const base = (nextInput && nextInput.value) || '';
    const next = base ? base.replace(/page=\d+/, 'page=' + (page + 1)) : '';
    document.querySelectorAll('[data-current-page]').forEach((node) => {
      node.value = page;
      node.dataset.currentPage = page;
    });
    if (next) {
      document.querySelectorAll('[data-next-url]').forEach((node) => {
        node.value = next;
        node.dataset.nextUrl = next;
      });
    }
    const button = document.querySelector('.js-load-more');
    if (!button) return;
    button.classList.remove('loading');
    if (page >= totalPages) button.remove();
    else button.removeAttribute('disabled');
  };

  // Las mismas re-inicializaciones que corre `load-more.js` al agregar una pagina.
  // Sin alguna de ellas la tarjeta se ve bien pero queda muerta: sin swatches, sin
  // fotos en el carrusel o con el bookmark apagado. Van sueltas en try/catch porque
  // dependen de secciones y settings que pueden no estar.
  const reinit = () => {
    try {
      if (typeof colorSwatches === 'function') colorSwatches();
    } catch (e) {}
    try {
      if (typeof loadeMoreSlider_G === 'function') loadeMoreSlider_G();
    } catch (e) {}
    try {
      if (typeof window.igHydrateCardMedia === 'function') window.igHydrateCardMedia(document);
    } catch (e) {}
    try {
      if (window.IGWishlist && typeof window.IGWishlist.sync === 'function') {
        window.IGWishlist.sync(document);
      }
    } catch (e) {}
    try {
      if (typeof updateLoadMoreCount === 'function') updateLoadMoreCount();
    } catch (e) {}
  };

  // ------------------------------------------------------------ restaurar
  const domReady = () =>
    document.readyState === 'loading'
      ? new Promise((resolve) => document.addEventListener('DOMContentLoaded', resolve, { once: true }))
      : Promise.resolve();

  const waitForGrid = () =>
    new Promise((resolve) => {
      const found = document.querySelector(GRID);
      if (found) return resolve(found);
      const tick = () => {
        const grid = document.querySelector(GRID);
        if (grid) return resolve(grid);
        if (document.readyState === 'complete') return resolve(null);
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });

  const restore = async (state, inflight) => {
    const grid = await waitForGrid();
    if (!grid) return;

    // Aterrizar YA: se reserva el alto que la grilla tenia y se scrollea, asi el
    // visitante cae en su fila en vez de mirar el tope de la coleccion mientras
    // llegan las paginas. El hueco se completa solo cuando insertamos.
    let reserved = false;
    let holding = false;
    if (state.gridHeight > 0) {
      grid.style.minHeight = state.gridHeight + 'px';
      reserved = true;
      // Y hay que SOSTENERLO: el documento se sigue parseando debajo, y cada
      // bloque que entra puede moverle el scroll al navegador. El loop se corta
      // solo cuando llegan las paginas o cuando el visitante toca algo.
      holding = true;
      const hold = () => {
        if (!holding || userMoved) return;
        jumpTo(Math.max(0, Math.round(state.scrollY || 0)));
        requestAnimationFrame(hold);
      };
      hold();
    }

    grid.setAttribute('aria-busy', 'true');

    // De aca en adelante hace falta el documento entero parseado, por dos motivos:
    //
    //  - El pager (`[data-total-pages]`, `[data-current-page]`, `.js-load-more`)
    //    se imprime DESPUES de la grilla, asi que a la altura de `waitForGrid()`
    //    todavia no existe. Leerlo ahi daba `totalPages = 1` y `writePager` borraba
    //    el boton: medido, "CARGAR MAS" despues de reponer no hacia nada (72 -> 72).
    //  - Insertar con `beforeend` mientras el parser todavia esta llenando la
    //    grilla deja las paginas 2 y 3 en el medio de la 1.
    //
    // No cuesta tiempo percibido: el visitante ya aterrizo en su fila alla por los
    // 500ms gracias a la reserva de altura; esto solo completa el hueco.
    await domReady();

    const button = document.querySelector('.js-load-more');
    if (button) {
      button.setAttribute('disabled', '');
      button.classList.add('loading');
    }

    const totalPages = num(document.querySelector('[data-total-pages]'), 1);
    const current = num(document.querySelector('[data-current-page]'), 1);

    const pages = inflight.pages;
    let htmls = [];
    try {
      htmls = await inflight.promise;
    } catch (e) {
      htmls = [];
    }

    // Se cortan en el primer hueco: dejar entrar la pagina 4 cuando fallo la 3
    // desordena la grilla y deja el pager mintiendo sobre lo que hay cargado.
    const parser = new DOMParser();
    let loaded = current;
    for (let i = 0; i < htmls.length; i++) {
      if (pages[i] <= current) continue;
      if (!htmls[i]) break;
      const incoming = parser.parseFromString(htmls[i], 'text/html').querySelector(GRID);
      if (!incoming) break;
      grid.insertAdjacentHTML('beforeend', incoming.innerHTML);
      loaded = pages[i];
    }

    holding = false;
    if (reserved) grid.style.minHeight = '';
    grid.removeAttribute('aria-busy');
    writePager(loaded, totalPages);

    // El scroll va ANTES del `reinit()`: el ancla solo necesita las cards en el
    // DOM, y `reinit()` bloquea ~1.2s inicializando los Swipers de cada tarjeta.
    scrollBack(state);
    reinit();
  };

  // -------------------------------------------------------------- guardar
  const snapshot = () => {
    const grid = document.querySelector(GRID);
    if (!grid) return null;
    const pages = num(document.querySelector('[data-current-page]'), 1);
    if (pages <= 1) return null;

    let anchor = null;
    let anchorTop = 0;
    const cards = grid.querySelectorAll(CARD);
    for (let i = 0; i < cards.length; i++) {
      const rect = cards[i].getBoundingClientRect();
      if (rect.bottom > 0) {
        const node = cards[i].matches(HANDLE) ? cards[i] : cards[i].querySelector(HANDLE);
        anchor = node ? node.getAttribute('data-product') : null;
        anchorTop = Math.round(rect.top);
        break;
      }
    }

    return {
      pages,
      scrollY: Math.round(window.scrollY),
      gridHeight: Math.round(grid.offsetHeight),
      anchor,
      anchorTop,
      t: Date.now(),
    };
  };

  const save = () => {
    const state = snapshot();
    // Si no hay nada que guardar hay que BORRAR, no salir en silencio: si no, un
    // estado viejo revive. Medido: cargo 3 paginas -> PDP -> vuelvo -> voy a otra
    // coleccion -> vuelvo a esta por un link normal (1 pagina) -> PDP -> Atras, y
    // reponia 3 paginas que el visitante no tenia, con sus fetches al pedo.
    if (!state) return store.remove(keyFor());
    store.prune();
    store.set(keyFor(), state);
  };

  // ----------------------------------------------------------- arranque
  const saved = store.get(keyFor());
  let launched = null;

  if (restorable(saved)) {
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    launched = fetchPages(2, Math.min(saved.pages, MAX_PAGES));
    restore(saved, launched);
  }

  const start = () => {
    if (!document.querySelector(GRID)) return;

    // `pagehide` cubre la navegacion normal y la entrada al bfcache; el
    // `visibilitychange` es la red para los casos en que el sistema mata la
    // pestania sin avisar (iOS).
    window.addEventListener('pagehide', save);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') save();
    });

    // Segundo intento con la clave recalculada: `facets.js` normaliza la URL con
    // `replaceState` en su propio `DOMContentLoaded` (le agrega la clausula de
    // disponibilidad), asi que un reload sobre una URL cruda con `filter.v.option.*`
    // no encontraba el estado en la lectura de arriba.
    if (launched) return;
    const late = store.get(keyFor());
    if (!restorable(late)) return;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    restore(late, fetchPages(2, Math.min(late.pages, MAX_PAGES)));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
